// src/components/pos/NotificacionesPos.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, User, Calendar, CreditCard, CheckCircle, ChevronRight, CheckCheck, Clock, MapPin, DollarSign } from 'lucide-react';
import { Transition } from '@headlessui/react';
import { useWebSocket } from '../../../../context/WebSocketContext';
import {
    fetchNotificaciones,
    confirmarNotificacion,
    updateNotificacion,
    Notificacion
} from '../../../../services/notificacionService';

const C = {
    brand:        '#61257b',   // Púrpura primario (Co Marca)
    brandDark:    '#3d1250',   // Púrpura oscuro
    brandLight:   '#8b3fa8',   // Púrpura suave
    accent1:      '#c1348b',   // Magenta / Acento principal
    accent1Dark:  '#8f1f64',   // Magenta oscuro
    accent2:      '#ea8406',   // Naranja cálido
    accent2Dark:  '#b86300',   // Naranja oscuro
    accent3:      '#ffd97c',   // Amarillo dorado
    accent3Dark:  '#f0b429',   // Dorado intenso
};

const colors = {
    primary:        C.brand,
    primaryDark:    C.brandDark,
    secondary:      C.brand,
    secondaryDark:  C.brandDark,   
    secondaryLight: C.accent1,
    accentDark:     C.accent2,
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
.notif-pos * { font-family:'Plus Jakarta Sans',sans-serif !important; box-sizing:border-box; }
@keyframes pulseNotif {
    0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.4); }
    50%      { box-shadow: 0 0 0 4px rgba(193,52,139,0); }
}
@keyframes notifSlideIn {
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes badgePop {
    0%   { transform: scale(0); }
    70%  { transform: scale(1.2); }
    100% { transform: scale(1); }
}
.notif-badge-anim { animation: badgePop 0.3s cubic-bezier(.16,1,.3,1) both; }
.notif-item { transition: background 0.15s, transform 0.15s; cursor: pointer; }
.notif-item:hover { background: #faf8fc !important; transform: translateX(2px); }
.notif-scroll::-webkit-scrollbar { width: 4px; }
.notif-scroll::-webkit-scrollbar-track { background: transparent; }
.notif-scroll::-webkit-scrollbar-thumb { background: rgba(97,37,123,.2); border-radius: 10px; }
`;

const formatRelativeTime = (date: Date): string => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'Ahora mismo';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const PosModal = ({ onClose, children, maxWidth = '500px' }: { onClose: () => void; children: React.ReactNode; maxWidth?: string }) =>
    createPortal(
        <div className="notif-pos" style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth, boxShadow: '0 32px 80px rgba(0,0,0,.2)', border: '1px solid #f1f5f9', overflow: 'hidden', animation: 'notifSlideIn 0.2s cubic-bezier(.16,1,.3,1)' }}>
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${C.brand}, ${C.accent1}, ${C.accent2}, ${C.accent3})` }} />
                {children}
            </div>
        </div>,
        document.body
    );

interface NotificacionesPosProps {
    isPOS: boolean;
    btnBg: string;
    btnHover: string;
    iconColor: string;
    /**
     * Sesión POS resuelta por el POSContext (vía NavbarPos). Confirmar una
     * reserva muta la cita: el backend exige el grant del operador sobre la
     * caja persistida de la cita; sin sesión no hay operador que identificar.
     */
    sesionPosId: number | null;
    /** Grant del operador identificado por PIN (POSContext); sin él no se confirma. */
    operadorToken: string | null;
}

const NotificacionesPos: React.FC<NotificacionesPosProps> = ({ isPOS, btnBg, btnHover, iconColor, sesionPosId, operadorToken }) => {
    const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [notifSeleccionada, setNotifSeleccionada] = useState<Notificacion | null>(null);
    const [confirmando, setConfirmando] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    const confirmandoRef = useRef(false);
    const mountedRef = useRef(true);
    const { connected, onCitaEvent } = useWebSocket();
    const puedeConfirmar = sesionPosId !== null && typeof operadorToken === 'string' && operadorToken.trim() !== '';

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const loadNotificaciones = useCallback(async () => {
        try {
            const data = await fetchNotificaciones();
            setNotificaciones(data);
        } catch (error) {
            console.error('[NotificacionesPos] Error cargando notificaciones:', error);
        }
    }, []);

    // Polling cada 10 segundos como respaldo
    useEffect(() => {
        loadNotificaciones();
        const interval = setInterval(loadNotificaciones, 10000);
        return () => clearInterval(interval);
    }, [loadNotificaciones]);

    // WebSocket: al recibir evento de cita actualizada, recargar notificaciones
    useEffect(() => {
        if (!onCitaEvent) return;
        const unsubscribe = onCitaEvent(() => {
            console.log('[NotificacionesPos] Evento WebSocket recibido → recargando notificaciones');
            loadNotificaciones();
        });
        return unsubscribe;
    }, [onCitaEvent, loadNotificaciones]);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest('[data-notif]')) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const unreadCount = notificaciones.filter(n => !n.leida).length;

    // Marcar una notificación como leída
    const marcarLeida = async (id: number) => {
        try {
            await updateNotificacion(id, { leida: true });
            setNotificaciones(prev =>
                prev.map(n => (n.id === id ? { ...n, leida: true } : n))
            );
        } catch (error) {
            console.error('Error al marcar como leída:', error);
        }
    };

    // Marcar todas como leídas
    const marcarTodasLeidas = async () => {
        const noLeidas = notificaciones.filter(n => !n.leida);
        for (const notif of noLeidas) {
            try {
                await updateNotificacion(notif.id, { leida: true });
            } catch (error) {
                console.error(`Error marcando notificación ${notif.id}:`, error);
            }
        }
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    };

    // Eliminar notificación (solo del estado local, no hay endpoint de borrado en el servicio)
    const eliminarNotif = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setNotificaciones(prev => prev.filter(n => n.id !== id));
    };

    // Al hacer clic en una notificación: marca como leída y abre el modal
    const handleNotifClick = (notif: Notificacion) => {
        if (!notif.leida) {
            marcarLeida(notif.id);
        }
        setNotifSeleccionada(notif);
        setModalAbierto(true);
        setShowDropdown(false);
    };

    // Confirmar cita desde el modal: solo con caja resuelta y operador
    // identificado (grant actual del POSContext); una sola petición en vuelo.
    const handleConfirmarCita = async () => {
        if (!notifSeleccionada || !puedeConfirmar || confirmandoRef.current) return;
        const notificacionId = notifSeleccionada.id;
        confirmandoRef.current = true;
        setConfirmando(true);
        setConfirmError(null);
        try {
            await confirmarNotificacion(notificacionId, operadorToken);
            if (!mountedRef.current) return;
            await loadNotificaciones(); // recargar para actualizar estado de la cita
            if (!mountedRef.current) return;
            setModalAbierto(false);
        } catch (error: any) {
            console.error('[NotificacionesPos] Error al confirmar cita:', error);
            if (!mountedRef.current) return;
            setConfirmError(
                error?.response?.data?.detail
                || error?.response?.data?.error
                || error?.message
                || 'Error al confirmar la cita',
            );
        } finally {
            confirmandoRef.current = false;
            if (mountedRef.current) setConfirmando(false);
        }
    };

    return (
        <div className="notif-pos">
            <style>{css}</style>

            {/* Botón campana (estilo POS, igual que antes) */}
            <div style={{ position: 'relative' }} data-notif="true">
                <button
                    className="nbp-nav-btn"
                    onClick={() => setShowDropdown(!showDropdown)}
                    title="Notificaciones"
                    style={{ background: btnBg }}
                    onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                    onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}
                >
                    <Bell size={17} color={iconColor} />
                    {unreadCount > 0 && (
                        <span className="notif-badge-anim" style={{
                            position: 'absolute', top: '-3px', right: '-3px', minWidth: '16px', height: '16px',
                            borderRadius: '100px', background: isPOS ? '#fff' : colors.primary,
                            color: isPOS ? colors.secondary : '#fff', fontSize: '.58rem', fontWeight: '900',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `2px solid ${isPOS ? colors.secondary : '#fff'}`,
                            padding: '0 3px',
                        }}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>

                {/* Dropdown de notificaciones (diseño TopBar) */}
                <Transition show={showDropdown} enter="transition ease-out duration-200" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-1">
                    <div data-dropdown-content="notif" style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                        width: '360px', background: '#fff', borderRadius: '20px',
                        border: '1.5px solid #f1f5f9', boxShadow: '0 20px 60px rgba(97,37,123,0.12)',
                        overflow: 'hidden', zIndex: 200, animation: 'notifSlideIn 0.2s cubic-bezier(.16,1,.3,1)'
                    }}>
                        <div style={{ height: '4px', background: `linear-gradient(90deg, ${C.brand}, ${C.accent1}, ${C.accent2}, ${C.accent3})` }} />

                        {/* Header */}
                        <div style={{ padding: '14px 18px', borderBottom: '1.5px solid #f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(97,37,123,.25)' }}>
                                        <Bell size={13} color="#fff" />
                                    </div>
                                    <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a' }}>Notificaciones</span>
                                    {unreadCount > 0 && (
                                        <span style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})`, color: '#fff', fontSize: '0.62rem', fontWeight: '800', borderRadius: '100px', padding: '2px 7px' }}>
                                            {unreadCount} nueva{unreadCount > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {unreadCount > 0 && (
                                <button onClick={marcarTodasLeidas} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '800', color: C.brand, padding: '4px 8px', borderRadius: '8px', transition: 'background 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = `${C.brand}10`; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                                    <CheckCheck size={12} /> Marcar todas
                                </button>
                            )}
                        </div>

                        {/* Lista de notificaciones */}
                        <div className="notif-scroll" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                            {notificaciones.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 20px', gap: '12px' }}>
                                    <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: '#f8fafc', border: '1.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Bell size={22} color="#cbd5e1" />
                                    </div>
                                    <p style={{ fontSize: '0.85rem', fontWeight: '800', color: '#94a3b8', textAlign: 'center' }}>Sin notificaciones</p>
                                    <p style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: '500', textAlign: 'center' }}>Las nuevas reservas aparecerán aquí</p>
                                </div>
                            ) : (
                                <div style={{ padding: '8px' }}>
                                    {notificaciones.map(notif => (
                                        <div key={notif.id} className="notif-item" onClick={() => handleNotifClick(notif)} style={{
                                            borderRadius: '14px', padding: '12px 14px', marginBottom: '4px',
                                            background: notif.leida ? '#fff' : `${C.brand}06`,
                                            border: notif.leida ? '1.5px solid transparent' : `1.5px solid ${C.brand}20`,
                                            position: 'relative'
                                        }}>
                                            {/* Botón eliminar */}
                                            <button onClick={(e) => eliminarNotif(notif.id, e)} style={{
                                                position: 'absolute', top: '10px', right: '10px', width: '20px', height: '20px', borderRadius: '6px',
                                                background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', color: '#cbd5e1', transition: 'color 0.15s, background 0.15s'
                                            }}
                                                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#94a3b8'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#cbd5e1'; }}>
                                                <X size={11} />
                                            </button>

                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', paddingRight: '24px' }}>
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '11px', flexShrink: 0,
                                                    background: notif.leida ? '#f8fafc' : `linear-gradient(135deg, ${C.brand}, ${C.accent1})`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    boxShadow: notif.leida ? 'none' : '0 3px 10px rgba(97,37,123,.22)'
                                                }}>
                                                    <Calendar size={16} color={notif.leida ? '#94a3b8' : '#fff'} />
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                                        <span style={{ fontSize: '0.78rem', fontWeight: '800', color: notif.leida ? '#374151' : '#0f172a' }}>
                                                            Nueva reserva confirmada
                                                        </span>
                                                    </div>
                                                    <p style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '6px' }}>
                                                        {notif.cita.servicio_info?.nombre || notif.cita.espacio_en_uso || 'Reserva'}
                                                    </p>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '6px' }}>
                                                        <span style={{ fontSize: '0.65rem', fontWeight: '700', background: '#f1f5f9', color: '#64748b', borderRadius: '6px', padding: '2px 7px', fontFamily: 'monospace' }}>
                                                            {notif.cita.codigo || `CITA-${notif.cita.id}`}
                                                        </span>
                                                        {notif.cita.fecha_inicio && (
                                                            <span style={{ fontSize: '0.65rem', fontWeight: '700', background: `${C.brand}12`, color: C.brand, border: `1px solid ${C.brand}22`, borderRadius: '6px', padding: '2px 7px' }}>
                                                                {new Date(notif.cita.fecha_inicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                {notif.cita.hora_inicio && ` · ${notif.cita.hora_inicio.slice(0, 5)}`}
                                                            </span>
                                                        )}
                                                        {notif.cita.total !== undefined && (
                                                            <span style={{ fontSize: '0.65rem', fontWeight: '700', background: `${C.accent2}15`, color: C.accent2Dark, border: `1px solid ${C.accent2}30`, borderRadius: '6px', padding: '2px 7px' }}>
                                                                Bs {Number(notif.cita.total).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: '500' }}>
                                                            {formatRelativeTime(new Date(notif.fecha_creacion))}
                                                        </span>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.68rem', fontWeight: '800', color: C.brand }}>
                                                            Ver detalles <ChevronRight size={10} />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {notificaciones.length > 0 && (
                            <div style={{ padding: '10px 16px', borderTop: '1.5px solid #f8fafc', display: 'flex', justifyContent: 'center' }}>
                                <button onClick={() => { setShowDropdown(false); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '800', color: C.brand, display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '10px', transition: 'background 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = `${C.brand}10`; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                                    Ver todas las reservas <ChevronRight size={12} />
                                </button>
                            </div>
                        )}

                        {!connected && (
                            <div style={{ padding: '8px', textAlign: 'center', fontSize: '.65rem', color: '#f59e0b', background: '#fffbeb', borderTop: '1px solid #fde68a' }}>
                                ⚡ Conectando en tiempo real...
                            </div>
                        )}
                    </div>
                </Transition>
            </div>

            {/* Modal de detalle de cita (rediseñado) */}
            {modalAbierto && notifSeleccionada?.cita && (
                <PosModal onClose={() => setModalAbierto(false)} maxWidth="520px">
                    <div style={{ padding: '24px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.02em' }}>Detalle de la Reserva</h3>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>Información completa de la cita</p>
                            </div>
                            <button onClick={() => setModalAbierto(false)} style={{ width: '32px', height: '32px', borderRadius: '10px', border: '1.5px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <X size={14} color="#64748b" />
                            </button>
                        </div>

                        {/* Grid de información */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                            {/* Cliente */}
                            <div style={{ gridColumn: 'span 2', background: '#f8fafc', borderRadius: '16px', padding: '14px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                    <User size={16} color={C.brand} />
                                    <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cliente</span>
                                </div>
                                <p style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a' }}>
                                    {notifSeleccionada.cita.cliente_info?.first_name} {notifSeleccionada.cita.cliente_info?.last_name}
                                </p>
                                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{notifSeleccionada.cita.cliente_info?.email}</p>
                            </div>

                            {/* Código de cita */}
                            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                    <CreditCard size={13} color={C.brand} />
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Código</span>
                                </div>
                                <p style={{ fontSize: '0.85rem', fontWeight: '800', fontFamily: 'monospace', color: '#0f172a' }}>{notifSeleccionada.cita.codigo || `CITA-${notifSeleccionada.cita.id}`}</p>
                            </div>

                            {/* Servicio */}
                            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                    <Calendar size={13} color={C.brand} />
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Servicio</span>
                                </div>
                                <p style={{ fontSize: '0.85rem', fontWeight: '700', color: '#374151' }}>{notifSeleccionada.cita.servicio_info?.nombre || 'Sin servicio'}</p>
                            </div>

                            {/* Fecha y hora */}
                            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                    <Clock size={13} color={C.brand} />
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Fecha y hora</span>
                                </div>
                                <p style={{ fontSize: '0.85rem', fontWeight: '700', color: '#374151' }}>
                                    {notifSeleccionada.cita.fecha_inicio ? new Date(notifSeleccionada.cita.fecha_inicio).toLocaleDateString() : '—'} {notifSeleccionada.cita.hora_inicio ? `· ${notifSeleccionada.cita.hora_inicio.slice(0, 5)}` : ''}
                                </p>
                            </div>

                            {/* Total */}
                            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                    <DollarSign size={13} color={C.brand} />
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Total</span>
                                </div>
                                <p style={{ fontSize: '0.95rem', fontWeight: '900', color: C.brand }}>Bs {Number(notifSeleccionada.cita.total || 0).toFixed(2)}</p>
                            </div>

                            {/* Estado */}
                            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                    <CheckCircle size={13} color={C.brand} />
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Estado</span>
                                </div>
                                <span style={{
                                    display: 'inline-block', fontSize: '0.7rem', fontWeight: '800', padding: '3px 10px', borderRadius: '100px',
                                    background: notifSeleccionada.cita.estado === 'Confirmada' ? '#f0fdf4' : `${C.accent2}15`,
                                    color: notifSeleccionada.cita.estado === 'Confirmada' ? '#16a34a' : C.accent2Dark,
                                    border: `1px solid ${notifSeleccionada.cita.estado === 'Confirmada' ? '#bbf7d0' : C.accent2 + '30'}`
                                }}>
                                    {notifSeleccionada.cita.estado}
                                </span>
                            </div>

                            {/* Espacio (si existe) */}
                            {notifSeleccionada.cita.espacio_en_uso && (
                                <div style={{ gridColumn: 'span 2', background: '#f8fafc', borderRadius: '14px', padding: '12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                        <MapPin size={13} color={C.brand} />
                                        <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#64748b' }}>Espacio</span>
                                    </div>
                                    <p style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>{notifSeleccionada.cita.espacio_en_uso}</p>
                                </div>
                            )}
                        </div>

                        {confirmError && (
                            <p role="alert" style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.78rem', fontWeight: 700 }}>
                                {confirmError}
                            </p>
                        )}

                        {/* Confirmar exige caja resuelta + operador identificado (grant del POSContext) */}
                        {notifSeleccionada.cita.estado === 'Pendiente' && !puedeConfirmar && (
                            <p role="note" style={{ padding: '10px 12px', borderRadius: '12px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.78rem', fontWeight: 700 }}>
                                {sesionPosId === null
                                    ? 'Necesitas una sesión POS resuelta para confirmar esta reserva.'
                                    : 'Identifica al operador de caja con su PIN para confirmar esta reserva.'}
                            </p>
                        )}
                        {notifSeleccionada.cita.estado === 'Pendiente' && puedeConfirmar && (
                            <button
                                onClick={handleConfirmarCita}
                                disabled={confirmando}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: '14px', border: 'none',
                                    background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})`,
                                    color: '#ffffff',
                                    fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    boxShadow: `0 6px 16px rgba(97,37,123,0.28)`,
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = `0 8px 20px rgba(97,37,123,0.38)`;
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = `0 6px 16px rgba(97,37,123,0.28)`;
                                }}
                            >
                                <CheckCircle size={16} /> Confirmar y Agendar
                            </button>
                        )}
                    </div>
                </PosModal>
            )}
        </div>
    );
};

export default NotificacionesPos;
